// M4-A §4 Pads, walking moves, batch drilling, SIMOPS gate, offsets, parent/child & frac hits. A shale plot (DSU) holds
// a pad of padCapacity(field) wells (pad_drilling upgrade; one well before it). Pads live in state.shale.pads keyed
// "fieldId:plot" (lazy); wells carry lateral.padUid. Assign hooks are no-ops outside shale fields or before 2000.
import { PAD, PARENT_CHILD, childPenalty } from '../../data/m4/rules.js';
import { padCapacity } from '../../data/m4/fields.js';
import { eqDef, fieldDef, stat, eraDollars, yf } from '../defs.js';
import { wellsOnField } from '../indexes.js';
import { spend, fmt$, log, notify } from '../util.js';
import { chance } from '../../core/rng.js';
import { on, sh, padKey, gearIn, upg, eff, SHALE_SIM, round } from './shaleUtil.js';
import { curveRate } from './shaleProd.js';
import { autoPlanLateral } from './shaleLaterals.js';

export const padOf = (state, fid, plot) => state.shale?.pads?.[padKey(fid, plot)] ?? null;
// M4 balance: before Pad Drilling a DSU still holds its full complement of laterals, but every well is drilled from
// its own single-well location (its own pad build, a full rig-down/move/up, no walking or batch savings) — the
// pre-2008 Barnett/Bakken practice. Was one well per DSU, which put a whole section's lease bonus on one well.
export function padCapacityNow(state, fid) {
  const fdef = fieldDef(fid);
  if (!fdef?.shale) return 1;
  return padCapacity(fdef);
}
export const multiWellPads = (state) => upg(state, 'pad_drilling');
export const plotWells = (state, fid, plot) => wellsOnField(state, fid).filter((w) => w.plot === plot);

// assignRig, before anything is spent: on a shale plot a busy/finished well means "drill the next pad slot".
// Returns null (default behaviour), { no } (refuse) or { newSlot: true }.
export function shalePadSlot(state, fieldId, plotIdx) {
  if (!on(state) || !fieldDef(fieldId)?.shale) return null;
  const plot = state.fields[fieldId]?.plots?.[plotIdx];
  const cur = plot?.wellId ? state.wells[plot.wellId] : null;
  if (!cur) return null;
  if (cur.status === 'SUSPENDED' && !cur.rigId) return null; // resume the suspended well
  const cap = padCapacityNow(state, fieldId);
  const n = plotWells(state, fieldId, plotIdx).filter((w) => w.status !== 'PLUGGED').length;
  if (n >= cap) return { no: `The spacing unit is full: it holds ${cap} wells.` };
  return { newSlot: true };
}

// Build a pad ahead of the rig (spec §4: $400k, 21 days) so the rig doesn't wait on the dirt work.
export function buildPadImpl(state, fieldId, plotIdx) {
  if (!on(state)) return { ok: false, msg: 'Pads are a shale-era practice.' };
  const fdef = fieldDef(fieldId);
  const plot = state.fields[fieldId]?.plots?.[plotIdx];
  if (!fdef?.shale || !plot) return { ok: false, msg: 'Pads are built on shale spacing units.' };
  if (!plot.owned) return { ok: false, msg: 'Lease the plot first.' };
  const S = sh(state);
  const key = padKey(fieldId, plotIdx);
  if (S.pads[key]) return { ok: false, msg: 'There is already a pad on this section.' };
  const cost = eraDollars(state, PAD.padBuildCost, PAD.baseYear);
  if (!spend(state, cost, 'capex')) return { ok: false, msg: `A pad costs ${fmt$(cost)}.` };
  S.pads[key] = { uid: key, fieldId, plot: plotIdx, builtDay: state.day, readyDay: state.day + PAD.padBuildDays, wellIds: [], cost: Math.round(cost) };
  return { ok: true, msg: `Building a drilling pad on the section (${fmt$(cost)}, ready in ${PAD.padBuildDays} days).` };
}

const walkDaysOf = (state, rig) => {
  const d = eqDef(rig.defId);
  if (stat(d, 'walking', false)) return stat(d, 'padWalkDays', 1);
  const g = gearIn(state, rig, 'walk');
  return g && stat(g, 'walking', false) ? stat(g, 'padWalkDays', 1.5) : null;
};

// assignRig, after the well exists: register the pad (build it on the first well), plan the lateral, and work out
// the rig-up time (walk to the next slot, skid, or rig down & move). Returns rig-up days or null (not ours).
export function shaleOnAssign(state, rig, well, rs) {
  if (!on(state) || !fieldDef(well.fieldId)?.shale || well.spudDay != null || well.contract) return null;
  const S = sh(state);
  const key = padKey(well.fieldId, well.plot);
  let pad = S.pads[key];
  const msgs = [];
  if (!pad) {
    const cost = eraDollars(state, PAD.padBuildCost, PAD.baseYear);
    spend(state, cost, 'capex', false);
    well.cost += cost;
    pad = S.pads[key] = { uid: key, fieldId: well.fieldId, plot: well.plot, builtDay: state.day, readyDay: state.day + PAD.padBuildDays, wellIds: [], cost: Math.round(cost) };
    msgs.push(`building the pad (${fmt$(cost)}, ${PAD.padBuildDays} days)`);
  }
  else if (!multiWellPads(state) && !pad.wellIds.includes(well.id) && pad.wellIds.length) { // M4 balance: a new single-well location
    const cost = eraDollars(state, PAD.padBuildCost, PAD.baseYear);
    spend(state, cost, 'capex', false);
    well.cost += cost;
    pad.cost = Math.round((pad.cost ?? 0) + cost);
    pad.readyDay = Math.max(pad.readyDay, state.day + PAD.padBuildDays);
    msgs.push(`building a new single-well location on the section (${fmt$(cost)}, ${PAD.padBuildDays} days; Pad Drilling puts them on one pad)`);
  }
  if (!pad.wellIds.includes(well.id)) pad.wellIds.push(well.id);
  const last = rig.m4Last;
  const samePad = multiWellPads(state) && !!last && last.fieldId === well.fieldId && last.plot === well.plot && pad.wellIds.length > 1;
  const walk = walkDaysOf(state, rig);
  let days;
  if (samePad) days = walk ?? stat(eqDef(rig.defId), 'skidDays', rs.rigUpDays) * (eff(state).rigMoveDaysMult ?? 1);
  else days = rs.rigUpDays * (pad.wellIds.length > 1 || last ? 2 : 1); // rig down, move, rig up (first rig-up: just up)
  days = Math.max(days, pad.readyDay - state.day);
  well.m4RigUpDays = round(days, 2);
  const plan = autoPlanLateral(state, well, rig);
  if (well.lateral) {
    well.lateral.padUid = key;
    well.lateral.batch = samePad && (stat(eqDef(rig.defId), 'batchDrilling', false) || upg(state, 'batch_drilling'));
  }
  if (samePad) msgs.push(walk != null ? `walking to the next slot (${walk} d)` : 'skidding to the next slot');
  if (plan) msgs.push(plan);
  if (well.lateral && /Not enough drill pipe/.test(plan ?? '')) notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'PIPE_SHORT', level: 'warn', text: `${well.name}: ${plan.slice(plan.indexOf('Not enough drill pipe'))}` }); // on screen, not just the log
  if (msgs.length) log(state, `${well.name}: ${msgs.join('; ').replace(/\.$/, '')}.`, 'info', { wellId: well.id });
  return Math.max(0.01, days);
}

// ---- spacing, offsets, parent/child -----------------------------------------------------------------------------
// Lateral wells in the same pad & bench, in drilling order (slot index = order).
export function benchMates(state, well) {
  const L = well.lateral;
  if (!L) return [];
  return wellsOnField(state, well.fieldId).filter((w) => w.lateral && w.lateral.padUid === L.padUid && w.lateral.bench === L.bench && w.status !== 'PLUGGED');
}
export function spacingOf(state, well) {
  const L = well.lateral;
  if (!L) return null;
  if (L.spacingFt) return L.spacingFt;
  const sd = fieldDef(well.fieldId)?.shale;
  return Math.round((sd?.plotWidthFt ?? 5280) / Math.max(1, benchMates(state, well).length));
}
export const interferenceOf = (fid) => fieldDef(fid)?.shale?.interferenceFt ?? 660;
// Offsets of a well: same pad & bench, distance = slot gap × spacing.
export function offsetsOf(state, well) {
  const mates = benchMates(state, well);
  const i = mates.indexOf(well);
  const sp = spacingOf(state, well);
  return mates.filter((w) => w !== well).map((w) => ({ well: w, distFt: Math.abs(mates.indexOf(w) - i) * sp }));
}
// Pressure depletion proxy of a producing parent: 1 − rate/qi on its type curve.
export function parentDepletion(state, w) {
  const p = w.prod;
  if (!p?.shale) return 0;
  const q = curveRate(p) / (p.permMult ?? 1);
  return Math.max(0, Math.min(1, 1 - q / Math.max(1e-6, p.qi)));
}
// Child status at frac time (spec §4): a producing offset within interferenceFt older than childAfterDays.
export function childInfo(state, well) {
  const I = interferenceOf(well.fieldId);
  let worst = null;
  for (const o of offsetsOf(state, well)) {
    const p = o.well.prod;
    if (!p?.shale || o.distFt >= I || (p.age ?? 0) <= PARENT_CHILD.childAfterDays) continue;
    const pen = childPenalty(o.distFt, I, parentDepletion(state, o.well)) * (eff(state).childPenaltyMult ?? 1);
    if (!worst || pen > worst.penalty) worst = { parentId: o.well.id, distFt: o.distFt, penalty: round(pen, 4) };
  }
  return worst ? { child: true, ...worst, mult: round(1 - worst.penalty, 4) } : { child: false, penalty: 0, mult: 1 };
}
// Each child frac: 25% frac hit on every producing parent within interference (−15% for 60 d, −5% for good).
export function fracHits(state, well) {
  const I = interferenceOf(well.fieldId);
  const hits = [];
  for (const o of offsetsOf(state, well)) {
    const p = o.well.prod;
    if (!p?.shale || o.distFt >= I || !['PRODUCING', 'SHUT_IN'].includes(o.well.status)) continue;
    if (!chance(state, PARENT_CHILD.parentHitChance)) continue;
    p.hitUntil = state.day + SHALE_SIM.parentHitDays;
    p.hitMult = 1 - PARENT_CHILD.parentHitLoss;
    p.permMult = round((p.permMult ?? 1) * (1 - PARENT_CHILD.parentHitPermanent), 4);
    hits.push(o.well.id);
    (o.well.lateral.parentOf ??= []).includes(well.id) || o.well.lateral.parentOf.push(well.id);
  }
  return hits;
}
// Producing offsets to shut in while this well is being pumped (spec §4).
export function shutInOffsets(state, well) {
  if (!PAD.shutInOffsetsDuringFrac) return [];
  const I = interferenceOf(well.fieldId);
  const out = [];
  for (const o of offsetsOf(state, well)) {
    const p = o.well.prod;
    if (!p || o.distFt >= I || o.well.status !== 'PRODUCING') continue;
    p.forcedUntil = Math.max(p.forcedUntil ?? 0, state.day + 1);
    p.fracShutInDays = (p.fracShutInDays ?? 0) + 1;
    out.push(o.well.id);
  }
  return out;
}
// Is a rig drilling on this pad right now (SIMOPS gate)?
export function rigOnPad(state, well) {
  if (!multiWellPads(state)) return false; // M4 balance: single-well locations — the next well's rig is on its own location
  const key = well.lateral?.padUid ?? padKey(well.fieldId, well.plot);
  return wellsOnField(state, well.fieldId).some((w) => w !== well && w.rigId && (w.lateral?.padUid ?? padKey(w.fieldId, w.plot)) === key && w.status !== 'AT_TD');
}
export function simopsAllowed(state) {
  return yf(state) >= PAD.simops.allowedFrom && upg(state, PAD.simops.requiresUpgrade);
}
// Interference the player knows: exact ±10% with spacing science + microseismic, else a vague range.
export function interferenceEstimate(state, fid) {
  const I = interferenceOf(fid);
  const known = upg(state, 'spacing_science') && upg(state, 'microseismic_monitoring');
  return known ? { known: true, lo: Math.round(I * 0.9), hi: Math.round(I * 1.1), ft: I } : { known: false, lo: Math.round(I * 0.5), hi: Math.round(I * 1.6), ft: null };
}
